/**
 * @fileoverview Shopping Cart Context Provider
 *
 * This file provides React Context for managing shopping cart state across the application.
 * It centralizes all cart-related operations including:
 * - Cart panel visibility management
 * - Cart item fetching and synchronization
 * - Integration with checkout workflow
 * - Shop item view coordination
 *
 * The context provides multiple hooks for different use cases:
 * - useCart: Full cart state and operations
 * - useCartRefresh: Only refresh functionality
 * - useCartState: Read-only state access
 */

import React, { createContext, useContext, useCallback, useState, useEffect } from "react";
import { ShoppingCartItem, BasketListResponse } from "./ShoppingCart.types";
import {
  convertBasketItemToCartItem,
  extractCartItemsFromCheckoutData,
  sortCartItemsByName,
} from "./ShoppingCart.utils";
import { useCloseShopItemView } from "pages/Shop/ShopItemView/ShopItemView";
import api from "api";
import { endpoints } from "config";
import { selectSignedIn } from "reducers/auth";
import { useSelector } from "react-redux";

/**
 * Cart context interface defining all available cart operations and state
 */
interface CartContextType {
  /** Array of items currently in the cart */
  items: ShoppingCartItem[];
  /** Whether cart data is currently loading */
  loading: boolean;
  /** Error message if cart operations failed */
  error: string | null;
  /** Function to refresh cart data from server */
  refreshCart: () => Promise<void>;
  /** Function to update cart items from checkout data */
  updateItemsFromCheckoutData: (checkoutData: any) => void;
  /** Whether the cart panel is currently open */
  isOpen: boolean;
  /** Function to open the cart panel */
  openCart: () => Promise<void>;
  /** Function to close the cart panel */
  closeCart: () => void;
  /** Function to toggle cart panel visibility */
  toggleCart: () => Promise<void>;
}

const CartContext = createContext<CartContextType | null>(null);

/**
 * CartProvider Component
 *
 * Provides shopping cart state and operations to child components through React Context.
 * Manages cart visibility, item fetching, and all cart-related operations.
 *
 * Features:
 * - Cart panel visibility state management
 * - Automatic cart data fetching and synchronization
 * - Integration with shop item view (closes item view when cart opens)
 * - Error handling and loading states
 * - Checkout data integration
 *
 * @param children - Child components that will have access to cart context
 */
export const CartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Core cart state
  const [items, setItems] = useState<ShoppingCartItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const isSignedIn = useSelector(selectSignedIn);

  // Hook to close shop item view when cart opens
  const { closeShopItemView } = useCloseShopItemView();

  /**
   * Fetches cart items from the server and updates local state
   * Handles API response parsing, error states, and item sorting
   */
  const fetchBasketItems = useCallback(async () => {
    setLoading(true);
    setError(null);

    // Only fetch cart items if user is signed in
    if (!isSignedIn) {
      setItems([]);
      setLoading(false);
      return;
    }

    try {
      const response = await api.get<BasketListResponse>(endpoints.shop.basket.list);

      if (response.data.status === "1" && response.data.details?.listData) {
        // Extract global currency symbol from basket response if available
        const globalCurrencySymbol = response.data.details?.currencySymbol;

        const cartItems = response.data.details.listData.map((item: any) =>
          convertBasketItemToCartItem(item, globalCurrencySymbol)
        );
        const sortedCartItems = sortCartItemsByName(cartItems);
        setItems(sortedCartItems);
      } else {
        // No items or API returned empty state
        setItems([]);
        if (response.data.errors?.length > 0) {
          console.error("Basket fetch errors:", response.data.errors);
        }
      }
    } catch (err) {
      console.error("Failed to fetch basket items:", err);
      setError("Failed to load cart items");
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [isSignedIn]);

  /**
   * Public API to refresh cart data
   * Wrapper around fetchBasketItems for external use
   */
  const refreshCart = useCallback(async () => {
    await fetchBasketItems();
  }, [fetchBasketItems]);

  /**
   * Updates cart items from checkout API data
   * Used during checkout flow to ensure cart items are in sync with server state
   *
   * @param checkoutData - Raw checkout data from API containing updated item information
   */
  const updateItemsFromCheckoutData = useCallback((checkoutData: any) => {
    const cartItems = extractCartItemsFromCheckoutData(checkoutData);
    if (cartItems.length > 0) {
      setItems(cartItems);
    }
  }, []);

  /**
   * Opens the cart panel
   * - Closes any open shop item view first
   * - Sets cart visibility to open
   * - Refreshes cart data to ensure it's current
   */
  const openCart = useCallback(async () => {
    closeShopItemView();
    setIsOpen(true);
    await fetchBasketItems();
  }, [fetchBasketItems, closeShopItemView]);

  /**
   * Closes the cart panel
   */
  const closeCart = useCallback(() => {
    setIsOpen(false);
  }, []);

  /**
   * Toggles cart panel visibility
   * Opens if closed, closes if open
   */
  const toggleCart = useCallback(async () => {
    if (!isOpen) {
      await openCart();
    } else {
      closeCart();
    }
  }, [isOpen, openCart, closeCart]);

  // Initialize cart data on component mount
  useEffect(() => {
    fetchBasketItems();
  }, [fetchBasketItems]);

  // Memoized context value to prevent unnecessary re-renders
  const contextValue: CartContextType = {
    items,
    loading,
    error,
    refreshCart,
    updateItemsFromCheckoutData,
    isOpen,
    openCart,
    closeCart,
    toggleCart,
  };

  return <CartContext.Provider value={contextValue}>{children}</CartContext.Provider>;
};

/**
 * Primary cart hook - provides access to all cart state and operations
 *
 * Must be used within a CartProvider component tree.
 * Throws an error if used outside of CartProvider context.
 *
 * @returns Complete cart context with state and operations
 * @throws Error if used outside CartProvider
 *
 * @example
 * ```tsx
 * const { items, isOpen, openCart, closeCart } = useCart();
 * ```
 */
export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error("useCart must be used within a CartProvider");
  }
  return context;
};

/**
 * Cart refresh hook - provides only the cart refresh functionality
 *
 * Gracefully handles cases where no CartProvider exists by returning a no-op function.
 * Useful for components that only need to refresh cart data without full cart state.
 *
 * @returns Object containing refreshCart function
 *
 * @example
 * ```tsx
 * const { refreshCart } = useCartRefresh();
 * await refreshCart(); // Refresh cart data
 * ```
 */
export const useCartRefresh = () => {
  const context = useContext(CartContext);
  if (!context) {
    // If no context provider, return a no-op function
    return {
      refreshCart: async () => {},
    };
  }
  return { refreshCart: context.refreshCart };
};

/**
 * Cart state hook - provides read-only access to cart state
 *
 * Returns only the state properties without operations.
 * Gracefully handles cases where no CartProvider exists by returning safe defaults.
 * Useful for components that only need to observe cart state without triggering operations.
 *
 * @returns Object containing cart state (isOpen, items, loading, error)
 *
 * @example
 * ```tsx
 * const { isOpen, items, loading } = useCartState();
 * if (loading) return <Spinner />;
 * ```
 */
export const useCartState = () => {
  const context = useContext(CartContext);
  if (!context) {
    // If no context provider, return safe defaults
    return {
      isOpen: false,
      items: [],
      loading: false,
      error: null,
    };
  }
  return {
    isOpen: context.isOpen,
    items: context.items,
    loading: context.loading,
    error: context.error,
  };
};
